import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { useAppSelector } from "./hooks";

const API_URL = process.env.REACT_APP_API_URL;

type NativePushTokenEvent = CustomEvent<{
  token: string;
  platform: "ios" | "android";
}>;

const REGISTRATION_TOAST_ID = "push-notification-registration";

const getRegistrationErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : "Unknown registration error";
  }

  const responseMessage = error.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }

  const validationErrors = error.response?.data?.errors;
  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors).flat().find(Boolean);
    if (typeof firstError === "string") return firstError;
  }

  return error.message || "The server did not accept this device";
};

/**
 * Registers the device's Expo push token against the signed-in user.
 *
 * The token is produced by the native shell (the app is a WebView) and handed
 * to us as `window.__nativePushToken` plus a `native-push-token` event. Either
 * side may be ready first, so we listen for the event *and* read the global.
 *
 * Registration is idempotent server-side, but we also guard here so a re-render
 * or a token refresh does not re-POST the same pair.
 */
export const usePushNotifications = (): void => {
  const authToken = useAppSelector((state) => state.auth.token);

  const pushTokenRef = useRef<string | null>(
    typeof window !== "undefined" ? window.__nativePushToken ?? null : null,
  );
  const registeredRef = useRef<string | null>(null);
  const previousAuthTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const register = async () => {
      const pushToken = pushTokenRef.current;
      if (!pushToken || !authToken) return;

      // Already registered this push token under this session.
      const key = `${authToken}:${pushToken}`;
      if (registeredRef.current === key) return;

      try {
        await axios.post(
          `${API_URL}/user/push-tokens`,
          {
            token: pushToken,
            platform: window.__nativePlatform,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        registeredRef.current = key;
        console.info("Push notification device registered", {
          endpoint: `${API_URL}/user/push-tokens`,
          platform: window.__nativePlatform,
        });
      } catch (error) {
        const message = getRegistrationErrorMessage(error);
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        const response = axios.isAxiosError(error)
          ? error.response?.data
          : undefined;

        // Keep this log sanitized: Axios's complete config includes the user's
        // Authorization header, so log only the useful response details.
        console.error("Push notification device registration failed", {
          endpoint: `${API_URL}/user/push-tokens`,
          platform: window.__nativePlatform,
          status,
          response,
          message,
        });
        toast.error(
          `Could not register this device for notifications${
            status ? ` (${status})` : ""
          }: ${message}`,
          { toastId: REGISTRATION_TOAST_ID },
        );
      }
    };

    const onNativePushToken = (event: Event) => {
      const { token, platform } = (event as NativePushTokenEvent).detail ?? {};
      if (!token) return;
      pushTokenRef.current = token;
      // A native FCM/APNs token may rotate while its Expo token string remains
      // unchanged. Force an idempotent POST so a row previously pruned after a
      // DeviceNotRegistered receipt is restored as soon as Expo refreshes it.
      registeredRef.current = null;
      if (platform) window.__nativePlatform = platform;
      void register();
    };

    window.addEventListener("native-push-token", onNativePushToken);

    // The shell may have injected the token before this component mounted.
    if (!pushTokenRef.current && window.__nativePushToken) {
      pushTokenRef.current = window.__nativePushToken;
    }
    void register();

    return () => {
      window.removeEventListener("native-push-token", onNativePushToken);
    };
  }, [authToken]);

  // On logout, detach this device so it stops receiving pushes for that user.
  useEffect(() => {
    const previousAuthToken = previousAuthTokenRef.current;
    previousAuthTokenRef.current = authToken;

    if (previousAuthToken && !authToken && pushTokenRef.current) {
      registeredRef.current = null;
      axios
        .delete(`${API_URL}/user/push-tokens`, {
          headers: { Authorization: `Bearer ${previousAuthToken}` },
          data: { token: pushTokenRef.current },
        })
        .catch(() => {
          // Best effort: the token is pruned server-side once Expo reports it dead.
        });
    }
  }, [authToken]);
};
